import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, finalize, Observable, of, OperatorFunction, startWith, Subscription, switchMap, tap, map } from 'rxjs';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { Routes } from 'src/app/accountancy/utilities/classes/routes.class';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { CashAdvanceInterface } from 'src/app/accountancy/utilities/interfaces/cash-advance.interface';
import { CashAdvanceService } from '../cash-advance-service/cash-advance.service';
import { ManageApproversService } from '../../../account-management/manage-approvers/manage-approvers-service/manage-approvers.service';
import { AccSimpleInputTextFormService } from 'src/app/accountancy/custom-components/acc-simple-input-text-form/acc-simple-input-text-form-service/acc-simple-input-text-form.service';
import { ApprovalStatusInterface } from '../../../../utilities/interfaces/global.interface';
import { LoginService } from 'src/app/accountancy/authentication/login/login-service/login.service';
import { AccMessageDialogService } from 'src/app/accountancy/custom-components/acc-message-dialog/acc-message-dialog-service/acc-message-dialog.service';
import { DisbursedService } from '../../disbursed-service/disbursed.service';
import { DisbursementVoucherService } from '../../disbursement-voucher/disbursement-voucher-service/disbursement-voucher.service';
import { MatDialog } from '@angular/material/dialog';
import { DisbursementVoucherComponent, DisbursementVoucherPassedData } from '../../disbursement-voucher/disbursement-voucher.component';
import { AccConfirmationService } from 'src/app/accountancy/custom-components/acc-confirmation/acc-confirmation-service/acc-confirmation.service';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';
import { LocalStorageApiService } from 'src/app/accountancy/utilities/services/local-storage-api/local-storage-api.service';

@Component({
  selector: 'app-view-cash-advance',
  templateUrl: './view-cash-advance.component.html',
  styleUrls: ['./view-cash-advance.component.scss']
})
export class ViewCashAdvanceComponent implements OnInit, OnDestroy {

  public readonly Helper = Helper;
  public readonly routes = Routes;
  public readonly cashAdvanceId = Helper.getIdOnSearchParam(this._aRoute, 'id');

  public readonly waitHandler = new WaitHandler();

  public readonly currentCA$ = new BehaviorSubject<CashAdvanceInterface | undefined>(undefined);
  private readonly reloadCurrentCA$ = new BehaviorSubject<undefined | void>(undefined);
  private readonly _subsGetCurrentCA: Subscription = this._getSubsGetCurrentCA();

  constructor(
    private _aRoute: ActivatedRoute,
    private _router: Router,
    private _caS: CashAdvanceService,
    private _mApprS: ManageApproversService,
    private _simpleFormS: AccSimpleInputTextFormService,
    private _matDialog: MatDialog,
    private _confirm: AccConfirmationService,
    private _gApiS: GlobalApiService,
    private _disburseS: DisbursedService,
    private _localStorageS: LocalStorageApiService,

    public loginS: LoginService,
  ) { }

  public ngOnInit(): void {
    if (!this.cashAdvanceId) this._router.navigate([this.routes.cashAdvance]);
  }

  public ngOnDestroy(): void {
    this._subsGetCurrentCA.unsubscribe();
  }

  public approveRequest() {
    const isRequesting$ = new BehaviorSubject<boolean>(false);
    const ref = this._simpleFormS.open({
      isRequesting$,
      title: "Approve Request",
      formClass: "min-w-[400px]",
      controls: [
        { name: "Remarks", inputType: "text", colClassName: "col-span-12" }
      ],
      onSubmit: formData => {
        const subs: Subscription = of(null).pipe(
          tap(() => {
            this.waitHandler.newRequest();
            isRequesting$.next(true);
          }),
          switchMap(() => this._caS.approveRequest(this.cashAdvanceId, formData["Remarks"]).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              isRequesting$.next(false);
              ref.close();
              subs.unsubscribe();
            }),
          ))
        ).subscribe();
      },
    });

    const subsRef: Subscription = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentCA$.next();
        subsRef.unsubscribe();
      }),
    ).subscribe();
  }

  public disapproveRequest() {
    const isRequesting$ = new BehaviorSubject<boolean>(false);
    const ref = this._simpleFormS.open({
      isRequesting$,
      title: "Disapprove Request",
      formClass: "min-w-[400px]",
      controls: [
        { name: "Remarks", inputType: "text", colClassName: "col-span-12" }
      ],
      onSubmit: formData => {
        const subs: Subscription = of(null).pipe(
          tap(() => {
            this.waitHandler.newRequest();
            isRequesting$.next(true);
          }),
          switchMap(() => this._caS.disapproveRequest(this.cashAdvanceId, formData["Remarks"]).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              isRequesting$.next(false);
              ref.close();
              subs.unsubscribe();
            }),
          ))
        ).subscribe();
      },
    });

    const subsRef: Subscription = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentCA$.next();
        subsRef.unsubscribe();
      }),
    ).subscribe();
  }

  public updateCA() {
    const isRequesting$ = new BehaviorSubject(false);
    const departmentList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);
    const detachmentList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);

    const subsDepartment: Subscription = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._gApiS.getDepartmentList().pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsDepartment.unsubscribe();
        }),
        tap(data => departmentList$.next(data.map(v => ({ value: `(${v.ID}) ${v.Name}`, key: `(${v.ID}) ${v.Name}` }))))
      ))
    ).subscribe();

    const subsDetachment: Subscription = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._gApiS.getDetachmentList().pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsDetachment.unsubscribe();
        }),
        tap(data => {
          const list = data?.map(v => ({ value: `(${v.ID}) ${v.Detachment}`, key: `(${v.ID}) ${v.Detachment}` })) ?? [];
          detachmentList$.next([{ value: '', key: 'No Detachment' }, ...list]);
        })
      ))
    ).subscribe();

    const currentCa = this.currentCA$.getValue();

    const chargeTo = currentCa?.ChargeTo ?? '-';
    const [rawDepartment, rawDetachment] = chargeTo.split('-');
    const department = (rawDepartment as string | undefined)?.trimRight() ?? '';
    const detachment = (rawDetachment as string | undefined)?.trimLeft() ?? '';

    return this._simpleFormS.open({
      title: 'Request Cash Advance',
      formClass: "min-w-[400px]",
      isRequesting$,
      controls: [
        { name: "Purpose", inputType: "text", initialValue: currentCa?.Purpose, required: true, colClassName: "col-span-12 mb-4" },
        { name: "Description", inputType: "text", initialValue: currentCa?.Description, required: true, colClassName: "col-span-12 mb-4" },
        { name: "Amount", label: "PHP", inputType: "number", initialValue: currentCa?.Amount, required: true, colClassName: "col-span-12 mb-4" },
        { name: "Department", inputType: "select", initialValue: department, required: true, colClassName: "col-span-12 md:col-span-6 mb-4", selectOptions$: departmentList$ },
        { name: "Detachment", inputType: "select", initialValue: detachment, colClassName: "col-span-12 md:col-span-6 mb-4", selectOptions$: detachmentList$ },
        { name: "Remarks", inputType: "text", initialValue: currentCa?.Remarks, colClassName: "col-span-12 mb-4" },
      ],
      onSubmit: (formData, ref) => {
        const formBody = {
          DateOfUse: Helper.date.format(new Date(), 1, '-'),
          TransactionDate: Helper.date.format(new Date(), 1, '-'),
          Purpose: String(formData["Purpose"]),
          Amount: Number(formData["Amount"]),
          DollarAmount: 0,
          Description: String(formData["Description"]),
          ChargeTo: `${formData["Department"]}${formData["Detachment"] ? ' - ' + formData["Detachment"] : ''}`,
          Remarks: String(formData["Remarks"]),
          // CntrlNumber: currentCa?.CntrlNumber,
          // UpdatedBy: this.loginS.userData?.ID,
        };

        const subs: Subscription = of(null).pipe(
          tap(() => this.waitHandler.newRequest()),
          // switchMap(() => this._gApiS.convertPhpToUsd(formBody.Amount).pipe(
          //   finalize(() => this.waitHandler.finishRequest()),
          //   tap(console.log),
          //   tap(() => this.waitHandler.newRequest()),
          // switchMap(values => this._caS.updateCashAdvanceRequest(currentCa?.CntrlNumber ?? '', { ...formBody, DollarAmount: values?.rates?.USD }).pipe(
          switchMap(values => this._caS.updateCashAdvanceRequest(currentCa?.CntrlNumber ?? '', formBody).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              this.reloadCurrentCA$.next();
              ref?.close();
              subs.unsubscribe();
            }),
          ))
          // )),
        ).subscribe();

      }
    }, true);
  }

  public viewVoucher() {
    this._localStorageS.object.set("currentVoucher", {
      type: "Cash Advance",
      disbursementVoucher: this.currentCA$.getValue()?.Voucher,
      controlNumber: this.cashAdvanceId,
    });

    const url = this._router.serializeUrl(this._router.createUrlTree(['home', 'disbursement', 'disbursement-voucher']));
    const windowFeatures = 'width=800,height=600,resizable,scrollbars';
    window.open(url, '_blank', windowFeatures);
  }

  public generateVoucher() {
    const currCa = this.currentCA$.getValue();
    const form = this._matDialog.open(DisbursementVoucherComponent, {
      data: {
        Type: "Cash Advance",
        CntrlNumber: this.cashAdvanceId,
        ApprovedAmount: currCa?.Amount,
      } as DisbursementVoucherPassedData,
      disableClose: true
    });

    const onClosedSubs: Subscription = form.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentCA$.next();
        onClosedSubs.unsubscribe();
      }),
    ).subscribe();
  }

  private _getSubsGetCurrentCA() {
    return combineLatest([
      this.reloadCurrentCA$.pipe(startWith()),
    ]).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._caS.getSingleCA(this.cashAdvanceId).pipe(
        finalize(() => this.waitHandler.finishRequest()),
        tap(data => {
          if (!data) throw ("Invalid Cash Advance.");

          const currCa: CashAdvanceInterface = {
            ...data,
            history: Helper.array.sortByPropertyName(data.history, "created_at", false),
          };

          this.currentCA$.next(currCa);
        }),
        catchError(() => this._gApiS.handleSingleGetError(() => this._router.navigate([this.routes.cashAdvance]))),
      ))
    ).subscribe();
  }
}
